import { Link } from 'react-router-dom';
import { Sparkles, TrendingUp } from 'lucide-react';

import { Badge, Button, Card, CardContent, CardHeader } from '@/components/ui';
import { useCurrentUser, useTier } from '@/lib/auth/AuthProvider';

const EXAMPLE_QUERIES = [
  'Which 200 MW hydrogen projects in Germany are at FID?',
  'Top 10 offshore wind investments in 2026.',
  'Compare carbon intensity of solar vs onshore wind across Europe.',
];

/**
 * Spec S04 §"External Client Portal home".
 *
 * - Quota indicator pill rendered globally in the header (see GlobalHeader).
 * - Example queries (3–5 admin-curated chips, clickable).
 * - Upgrade banner for the Free tier.
 * - Watchlist + saved queries land in v2.
 */
export function ClientPortalHome() {
  const user = useCurrentUser();
  const tier = useTier();

  return (
    <div className="mx-auto max-w-3xl px-6 py-8">
      <div className="mb-6">
        <h1 className="text-display text-brand-900">Welcome{user?.name ? `, ${user.name}` : ''}</h1>
        <p className="mt-1 text-body text-slate-600">
          Ask the ETT knowledge graph anything about the energy transition.
        </p>
      </div>

      {tier === 'free' && <UpgradeBanner />}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" aria-hidden />
            <h2 className="text-h2 text-brand-900">Try a question</h2>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {EXAMPLE_QUERIES.map((q) => (
              <li key={q}>
                <Button asChild variant="secondary" className="w-full justify-start text-left">
                  <Link to={`/app/chat?prefill=${encodeURIComponent(q)}`}>{q}</Link>
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {tier === 'analyst' || tier === 'enterprise' ? (
        <Card className="mt-4">
          <CardHeader>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-accent" aria-hidden />
              <h2 className="text-h2 text-brand-900">Watchlist</h2>
              <Badge variant="info">v2</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-body text-slate-600">
              New activity on watched entities will appear here in v2.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function UpgradeBanner() {
  const billingUrl = import.meta.env.VITE_EXTERNAL_BILLING_URL || '#';
  return (
    <div className="mb-4 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div>
        <p className="text-body font-medium text-amber-900">You're on the Free tier</p>
        <p className="text-small text-amber-800">
          Upgrade to Analyst for 30 queries/day, Map + Graph responses, and Watchlist.
        </p>
      </div>
      <Button asChild variant="primary">
        <a href={billingUrl} target="_blank" rel="noopener noreferrer">
          Upgrade
        </a>
      </Button>
    </div>
  );
}
